import {
    AdvanceDeduction,
    DocumentType,
    Invoice,
    InvoiceDetails,
    InvoiceLine,
    InvoiceStatus,
    InvoiceTotals,
    PriorRetention,
} from '../types';
import { Cents, formatCents } from './money';
import {
    calculateInvoiceTotals,
    computeRetention,
    effectiveRetentionPercent,
    RetentionInput,
    RetentionTotals,
    ValidationMessage,
} from './invoiceLogic';
import { mirrorAllocations } from './allocationLogic';

/**
 * Storno, Korrektur und Gutschriften.
 *
 * Unter GoBD wird nicht geloescht und nicht ueberschrieben, sondern storniert.
 * Jede Berichtigung ist ein EIGENER Beleg mit eigener Nummer, eigenem PDF und
 * einer Verknuepfung zum Ursprungsbeleg. Die Historie zeigt danach ein Paar,
 * nicht eine geaenderte Zeile.
 *
 * Zwei Begriffe, die im Deutschen dasselbe Wort teilen und nichts miteinander
 * zu tun haben:
 *   - KAUFMAENNISCHE Gutschrift (COMMERCIAL_CREDIT_INVOICE): wir mindern
 *     nachtraeglich unsere eigene Forderung. Ein gewoehnlicher Korrekturbeleg.
 *   - Gutschrift im UMSATZSTEUERLICHEN Sinn (SELF_BILLING_CREDIT_NOTE, §14
 *     Abs. 2 Satz 2 UStG): der LEISTUNGSEMPFAENGER rechnet ueber unsere
 *     Leistung ab. Das ist eine Rechnung, nur von der anderen Seite
 *     ausgestellt, und der Umsatz ist positiv.
 * Sie zu verwechseln dreht das Vorzeichen des Umsatzes.
 *
 * Pur: kein Date.now(), keine Datenbank.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Belegarten
// ═══════════════════════════════════════════════════════════════════════════

/** Belegarten, die einen bestehenden Beleg berichtigen. */
export const CORRECTION_DOCUMENT_TYPES = new Set<DocumentType>([
    'CANCELLATION_INVOICE',
    'CORRECTION_INVOICE',
    'COMMERCIAL_CREDIT_INVOICE',
]);

/**
 * Belegarten, deren Betraege den Umsatz MINDERN.
 *
 * SELF_BILLING_CREDIT_NOTE gehoert bewusst NICHT dazu: sie ist eine Abrechnung
 * ueber unsere eigene Leistung und erhoeht den Umsatz.
 */
export const NEGATIVE_DOCUMENT_TYPES = new Set<DocumentType>([
    'CANCELLATION_INVOICE',
    'COMMERCIAL_CREDIT_INVOICE',
]);

export const isCreditNote = (type: DocumentType): boolean =>
    type === 'COMMERCIAL_CREDIT_INVOICE' || type === 'SELF_BILLING_CREDIT_NOTE';

/**
 * Gibt dieser Beleg ausschliesslich Menge ins Projekt-LV zurueck, ohne welche
 * zu beanspruchen?
 *
 * An den ZEILEN entschieden, nicht an der Belegart. Die Korrekturrechnung ist
 * der Grund: sie kann nach unten gehen (dann gibt sie zurueck) oder nach oben
 * (dann beansprucht sie), und ihre Belegart sagt darueber nichts. Sie deshalb
 * in NEGATIVE_DOCUMENT_TYPES aufzunehmen waere falsch — jene Menge steuert auch
 * das Umsatzvorzeichen (signedGrossCents) und die Abschlagsbehandlung; eine
 * Aufwaertskorrektur wuerde dort mit falschem Vorzeichen zaehlen.
 *
 * Nur wer Menge BEANSPRUCHT, kann den Leistungsstand ueberziehen. Ein Beleg,
 * der ausschliesslich zurueckgibt, entspannt die Lage — die roten Befunde des
 * Projektchecks ("mehr abgerechnet als erbracht", "Menge doppelt beansprucht")
 * sind auf ihn nicht anwendbar und haben ausgerechnet den Beleg gesperrt, der
 * sie aufloest.
 *
 * Zeilen ohne Projektbezug bleiben aussen vor: Pauschalen und Abschlaege
 * beruehren das LV nicht. Ein Beleg ganz ohne LV-Bezug gilt hier NICHT als
 * "gibt zurueck" — er soll wie bisher am Projektcheck haengenbleiben.
 *
 * Ebenso aussen vor bleiben reine Betragskorrekturen
 * (quantityInformational): ihre Menge benennt die urspruengliche Position und
 * wird weder beansprucht noch zurueckgegeben. Als beanspruchte Menge gezaehlt
 * haette eine einzelne solche Zeile den ganzen Beleg wieder unter die
 * Sperrwirkung des Projektchecks gestellt, obwohl er im LV nichts anfasst.
 */
export const onlyReleasesProjectQuantity = (
    lines: Pick<
        InvoiceLine,
        'kind' | 'serviceId' | 'projectId' | 'quantity' | 'quantityInformational'
    >[],
): boolean => {
    const lv = lines.filter(
        (l) => l.kind === 'SERVICE' && !!l.serviceId && !!l.projectId && !l.quantityInformational,
    );
    return (
        lv.some((l) => (l.quantity ?? 0) < 0) && !lv.some((l) => (l.quantity ?? 0) > 0)
    );
};

// ═══════════════════════════════════════════════════════════════════════════
// Zeilen spiegeln
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Kehrt alle Betraege und Mengen um.
 *
 * Menge UND Betrag werden gespiegelt, nicht nur der Betrag: die Rueckschreibung
 * ins Projekt liest die Menge, und ein Storno muss den Leistungsstand exakt um
 * dieselbe Menge mindern, um die er beim Ausstellen erhoeht wurde.
 */
export const mirrorLines = (lines: InvoiceLine[], makeId: () => string): InvoiceLine[] =>
    lines.map((l) => ({
        ...l,
        id: makeId(),
        quantity: l.quantity != null ? -l.quantity : undefined,
        netCents: -l.netCents,
        // Beim vollen Storno waere der Betrag auch aus Menge x Preis richtig.
        // Bei der kaufmaennischen Gutschrift ueber einen TEILBETRAG ist er es
        // nicht — dort gibt der Nutzer die Summe vor, waehrend Menge und Preis
        // die urspruengliche Position benennen.
        netCentsExplicit: true,
        // Auch die Zuordnung zum Leistungsprotokoll umkehren. Ohne sie bliebe
        // die Leistung nach einem Storno als abgerechnet stehen, waehrend
        // quantityBilled bereits wieder gemindert ist: die Summe stimmte, aber
        // der Zeitraumfilter faende die Leistung nie wieder.
        allocations: mirrorAllocations(l.allocations),
    }));

/**
 * Kehrt die Abschlagsabzuege eines Belegs um.
 *
 * Gehoert zwingend zu mirrorLines: eine Schlussrechnung fordert nur den um die
 * Abschlaege geminderten Betrag. Wuerde das Storno nur die Leistungszeilen
 * spiegeln, stuende dort der volle Bruttobetrag als Erstattung — der Kunde
 * bekaeme den Abzugsbetrag ein zweites Mal gutgeschrieben.
 *
 * Die Belegnummer und das Datum des Abschlags bleiben stehen: sie benennen den
 * Vorgang, der rueckabgewickelt wird, und duerfen sich dabei nicht aendern.
 */
export const mirrorDeductions = (deductions: AdvanceDeduction[]): AdvanceDeduction[] =>
    deductions.map((d) => ({
        ...d,
        netCents: -d.netCents,
        taxCents: -d.taxCents,
        grossCents: -d.grossCents,
    }));

/** Zeilen gelten als dieselbe Position, wenn Herkunft und Text uebereinstimmen. */
const correctionKey = (l: InvoiceLine): string =>
    [l.serviceId ?? '', l.projectId ?? '', l.lvPosition ?? '', l.title ?? ''].join('|');

/**
 * Zeilen einer kaufmaennischen Gutschrift.
 *
 * Wie mirrorLines kehrt das die Betraege um — aber NICHT bedingungslos die
 * Menge. Der Unterschied ist der Grund, warum es diese Funktion gibt:
 *
 *   • VOLLE Gutschrift (der erstattete Betrag deckt die Position ganz): die
 *     Position entfaellt. Menge und Zuordnung werden gespiegelt, die Leistung
 *     ist im Projekt wieder offen — dieselbe Wirkung wie beim Storno.
 *   • TEILGUTSCHRIFT: eine Preisminderung. Sie sagt ueber die MENGE nichts.
 *     Die Leistung ist erbracht und bleibt abgerechnet; nur das Geld sinkt.
 *
 * Vorher lief beides ueber mirrorLines, und das spiegelte die Menge immer.
 * Eine Gutschrift ueber 100 von 360 EUR gab damit die GANZE Position ins
 * Projekt zurueck: die Leistung stand wieder offen, obwohl 260 EUR zu Recht
 * abgerechnet waren, und der naechste Rechnungslauf haette sie ein zweites Mal
 * gestellt. Auf dem Beleg stimmte der Betrag — der Fehler stand im Projekt.
 *
 * Die Teilzeile traegt Menge und Einzelpreis weiter: der Empfaenger muss die
 * Position wiederfinden, und die E-Rechnung braucht BT-129/BT-146. Dass sie
 * nichts beansprucht, sagt `quantityInformational` — dieselbe Loesung wie bei
 * der reinen Betragskorrektur in buildCorrectionLines.
 *
 * Bewusst NICHT anteilig gerechnet (Menge mal Erstattung durch Betrag): das
 * ergaebe krumme Teilmengen wie 0,83 Stueck, die im LV niemand mehr zuordnen
 * kann — und eine Preisminderung meint gerade nicht, dass weniger geleistet
 * wurde.
 */
export const buildCreditLines = (
    original: InvoiceLine[],
    requested: InvoiceLine[],
    makeId: () => string,
): InvoiceLine[] => {
    const byKey = new Map<string, InvoiceLine>();
    for (const l of original) {
        if (l.kind !== 'SERVICE') continue;
        byKey.set(correctionKey(l), l);
    }

    return requested.map((l) => {
        const before = byKey.get(correctionKey(l));
        // Ohne Gegenstueck gilt die Zeile als TEILgutschrift. Konservativ und
        // mit Absicht: nicht freigeben heisst zu wenig abrechnen und faellt
        // beim naechsten Lauf auf. Faelschlich freigeben heisst doppelt
        // abrechnen — der Kunde zahlt zweimal, und das merkt niemand.
        const full = before != null && Math.abs(l.netCents) >= Math.abs(before.netCents);

        return {
            ...l,
            id: makeId(),
            quantity: full && l.quantity != null ? -l.quantity : l.quantity,
            // Ausdruecklich in BEIDE Richtungen gesetzt: der Spread bringt den
            // Schalter einer bereits informatorischen Zeile mit, und eine volle
            // Gutschrift muss ihre Menge buchen duerfen.
            quantityInformational: full ? undefined : true,
            netCents: -l.netCents,
            // Bei der Teilgutschrift gibt der Nutzer die Summe vor, waehrend
            // Menge und Preis die urspruengliche Position benennen — aus ihnen
            // laesst sich der Betrag nicht nachrechnen.
            netCentsExplicit: true,
            // Die Zuordnung zum Leistungsprotokoll nur bei der vollen
            // Gutschrift umkehren. Sonst bliebe die Leistung nach der Rueckgabe
            // als abgerechnet stehen, waehrend quantityBilled schon gemindert
            // ist: die Summe stimmte, aber der Zeitraumfilter faende sie nie
            // wieder. Bei der Teilgutschrift wird nichts zurueckgegeben, also
            // darf auch nichts umgekehrt werden — die geerbte, POSITIVE
            // Zuordnung laese die Rueckschreibung als Buchung.
            allocations: full ? mirrorAllocations(before?.allocations ?? l.allocations) : undefined,
        };
    });
};

/**
 * Differenzzeilen einer Korrekturrechnung.
 *
 * Eine Korrektur ersetzt den Ursprungsbeleg nicht, sie ergaenzt ihn um die
 * DIFFERENZ. Danach stehen beide Belege nebeneinander, und ihre Summe ist der
 * richtige Betrag — das ist der pruefbare Zustand.
 *
 * Zeilen ohne Aenderung entfallen: eine Differenzzeile ueber 0,00 € waere auf
 * dem Beleg nur Rauschen.
 */
export const buildCorrectionLines = (
    original: InvoiceLine[],
    corrected: InvoiceLine[],
    makeId: () => string,
): InvoiceLine[] => {
    const byKey = new Map<string, InvoiceLine>();
    for (const l of original) {
        if (l.kind !== 'SERVICE') continue;
        byKey.set(correctionKey(l), l);
    }

    const out: InvoiceLine[] = [];
    for (const c of corrected) {
        if (c.kind !== 'SERVICE') continue;
        const key = correctionKey(c);
        const before = byKey.get(key);
        byKey.delete(key);

        const deltaNet = c.netCents - (before?.netCents ?? 0);
        const deltaQty = (c.quantity ?? 0) - (before?.quantity ?? 0);
        if (deltaNet === 0 && deltaQty === 0) continue;

        out.push({
            ...c,
            id: makeId(),
            // Die Menge der Differenzzeile ist die MENGENAENDERUNG. Aendert sich
            // die Menge nicht — der Regelfall, denn der Korrekturdialog laesst
            // ausschliesslich Betraege bearbeiten —, benennt die Zeile die
            // urspruengliche Position weiter (Menge, Einheit, Einzelpreis als
            // Kontext fuer den Empfaenger), beansprucht aber keine Menge. Genau
            // das sagt quantityInformational.
            //
            // Ohne den Schalter trug die Zeile die volle Positionsmenge POSITIV
            // und wirkte wie eine zweite Abrechnung derselben Leistung: aus 20
            // abgerechneten wurden 40. War nichts mehr offen — der Normalfall
            // nach vollstaendiger Abrechnung —, scheiterte stattdessen die
            // Ueberbuchungsschranke ("offen sind nur 0"), und eine reine
            // Betragskorrektur liess sich ueberhaupt nicht ausstellen.
            //
            // Ausdruecklich in BEIDE Richtungen gesetzt: der Spread brachte den
            // Schalter einer bereits korrigierten Zeile mit, und eine echte
            // Mengenaenderung darf ihn nicht erben — sie will gebucht werden.
            quantity: deltaQty !== 0 ? deltaQty : c.quantity,
            quantityInformational: deltaQty === 0 ? true : undefined,
            netCents: deltaNet,
            // Zwingend: aendert der Nutzer nur den Betrag und nicht die Menge,
            // widerspricht deltaNet dem Produkt aus Menge und Einzelpreis.
            // Ohne diesen Schalter rechnet recalculateLines beim Anlegen des
            // Entwurfs den Differenzbetrag weg und stellt statt einer Minderung
            // die volle urspruengliche Position erneut in Rechnung.
            netCentsExplicit: true,
            // Weder die Zuordnung noch der Vorabgerechnet-Stand (samt seiner
            // Deckung durch Vorbelege) duerfen aus der korrigierten Zeile
            // mitkommen: alle drei beschreiben die VOLLE Position des
            // Ursprungsbelegs, diese Zeile aber nur die Differenz.
            //
            // Bei positiver Differenz fiel das nicht auf — planAllocations
            // ueberschreibt die Zuordnung ohnehin. Bei negativer schon: die
            // geerbte Zuordnung ist positiv, und die Rueckschreibung nimmt sie
            // wie gebucht, was den Abrechnungsstand ERHOEHT statt ihn zu
            // mindern. Der geerbte Vorabgerechnet-Stand wiederum liesse die
            // Minderung ganz verschwinden (Zeile -2, vorabgerechnet 10 ergaebe
            // "nichts zu buchen"). Ohne beide bleibt die Differenzmenge
            // massgeblich — und genau die soll gebucht werden.
            allocations: undefined,
            previouslyBilledQty: undefined,
            previouslyInvoicedQty: undefined,
            title: before ? `${c.title} (Korrektur)` : `${c.title} (nachträglich)`,
        });
    }

    // Was im Ursprungsbeleg stand und in der Korrektur fehlt, wird in voller
    // Hoehe zurueckgenommen. Stillschweigend weglassen waere ein Betrag, der
    // niemandem auffaellt.
    for (const removed of byKey.values()) {
        out.push({
            ...removed,
            id: makeId(),
            quantity: removed.quantity != null ? -removed.quantity : undefined,
            // Diese Menge WILL gebucht werden — sie gibt die Position frei.
            // Der Schalter darf deshalb nicht aus einer schon korrigierten
            // Ursprungszeile mitkommen; sonst bliebe die Menge im Projekt
            // stehen, obwohl die Position entfaellt.
            quantityInformational: undefined,
            netCents: -removed.netCents,
            netCentsExplicit: true,
            // Diese Zeile nimmt die Position VOLLSTAENDIG zurueck — dasselbe,
            // was mirrorLines beim Storno tut, also auch dieselbe Umkehr der
            // Zuordnung. Der Spread brachte sie positiv mit: die
            // Rueckschreibung las sie als Buchung und ERHOEHTE den
            // Abrechnungsstand, obwohl die Position entfaellt. Ein stiller
            // Datenfehler — der Betrag auf dem Beleg stimmte.
            allocations: mirrorAllocations(removed.allocations),
            title: `${removed.title} (entfällt)`,
        });
    }

    return out;
};

// ═══════════════════════════════════════════════════════════════════════════
// Pruefungen
// ═══════════════════════════════════════════════════════════════════════════

/** Status, aus denen heraus storniert werden darf. */
const CANCELLABLE: InvoiceStatus[] = [
    'ISSUED',
    'SENT',
    'PARTIALLY_PAID',
    'PAID',
    'OVERDUE',
    'DISPUTED',
    'PARTIALLY_CREDITED',
];

/**
 * Prueft, ob ein Beleg storniert werden darf.
 *
 * Der wichtigste Fall ist der zweite Storno: er wuerde einen zweiten
 * Stornobeleg erzeugen und den Leistungsstand ein zweites Mal mindern — die
 * abgerechnete Menge fiele unter null. Deshalb ist der Status hier ein hartes
 * Kriterium und keine Warnung.
 */
export const validateCancellation = (
    original: Pick<Invoice, 'status' | 'invoiceNumber' | 'documentType' | 'paidCents'>,
): ValidationMessage[] => {
    const out: ValidationMessage[] = [];
    const err = (code: string, message: string) => out.push({ code, level: 'error', message });
    const warn = (code: string, message: string) => out.push({ code, level: 'warning', message });

    if (!original.invoiceNumber) {
        err(
            'not_issued',
            'Nur ausgestellte Rechnungen können storniert werden. Ein Entwurf wird gelöscht, nicht storniert.',
        );
    }
    if (original.status === 'CANCELLED') {
        err('already_cancelled', 'Dieser Beleg ist bereits storniert.');
    } else if (original.status === 'SUPERSEDED_BY_FINAL_INVOICE') {
        err(
            'already_superseded',
            'Dieser Abschlag wurde bereits durch eine Schlussrechnung abgelöst. Zuerst die Schlussrechnung stornieren.',
        );
    } else if (!CANCELLABLE.includes(original.status)) {
        err(
            'not_cancellable',
            `Ein Beleg im Status "${original.status}" kann nicht storniert werden.`,
        );
    }

    if (original.documentType === 'CANCELLATION_INVOICE') {
        err(
            'cancel_of_cancel',
            'Ein Stornobeleg wird nicht storniert. Der ursprüngliche Vorgang wird stattdessen neu berechnet.',
        );
    }

    if (original.paidCents > 0) {
        warn(
            'paid_before_cancel',
            `Auf diesen Beleg sind bereits ${formatCents(original.paidCents)} eingegangen. Der Betrag wird durch das Storno zum Kundenguthaben und muss erstattet oder verrechnet werden.`,
        );
    }

    return out;
};

/** Prueft eine Korrekturrechnung. */
export const validateCorrection = (
    original: Pick<Invoice, 'status' | 'invoiceNumber' | 'documentType'>,
    correctionLines: InvoiceLine[],
): ValidationMessage[] => {
    const out: ValidationMessage[] = [];
    const err = (code: string, message: string) => out.push({ code, level: 'error', message });

    if (!original.invoiceNumber) {
        err(
            'not_issued',
            'Nur ausgestellte Rechnungen werden korrigiert. Ein Entwurf wird bearbeitet.',
        );
    }
    if (original.status === 'CANCELLED') {
        err(
            'corrected_cancelled',
            'Ein stornierter Beleg wird nicht korrigiert. Es wird eine neue Rechnung gestellt.',
        );
    }
    if (correctionLines.length === 0) {
        err('no_difference', 'Es gibt keine Abweichung zum Ursprungsbeleg — nichts zu korrigieren.');
    }

    const totals = calculateInvoiceTotals(correctionLines);
    if (correctionLines.length > 0 && totals.grossTotalCents === 0) {
        err(
            'zero_difference',
            'Die Korrektur ergibt in Summe 0,00 €. Ein Beleg ohne betragsmäßige Wirkung wird nicht ausgestellt.',
        );
    }

    return out;
};

/**
 * Prueft die Verwendung eines Kundenguthabens.
 *
 * Die harte Grenze zieht die Datenbank (CHECK auf customer_credits). Diese
 * Pruefung dient der Oberflaeche, damit der Nutzer den Grund sieht, bevor die
 * Transaktion scheitert.
 */
export const validateCreditUsage = (
    credit: { amountCents: Cents; usedCents: Cents; customerId: string },
    target: Pick<Invoice, 'customerId' | 'status' | 'totals' | 'paidCents' | 'creditedCents'>,
    amountCents: Cents,
): ValidationMessage[] => {
    const out: ValidationMessage[] = [];
    const err = (code: string, message: string) => out.push({ code, level: 'error', message });

    const available = credit.amountCents - credit.usedCents;
    if (amountCents <= 0) {
        err('amount_invalid', 'Der Verrechnungsbetrag muss größer als 0,00 € sein.');
    }
    if (amountCents > available) {
        err(
            'exceeds_credit',
            `Das Guthaben trägt nur noch ${formatCents(available)}, verrechnet werden sollen ${formatCents(amountCents)}.`,
        );
    }
    if (credit.customerId !== target.customerId) {
        err(
            'wrong_customer',
            'Das Guthaben gehört einem anderen Kunden. Guthaben werden nicht zwischen Kunden verschoben.',
        );
    }

    const open = Math.max(0, target.totals.payableCents - target.paidCents - target.creditedCents);
    if (amountCents > open) {
        err(
            'exceeds_open',
            `Die Zielrechnung hat nur noch ${formatCents(open)} offen. Ein Überhang würde eine zweite Überzahlung erzeugen.`,
        );
    }

    return out;
};

/** Summe eines Belegs mit dem Vorzeichen seiner Belegart. */
export const signedGrossCents = (invoice: Pick<Invoice, 'documentType' | 'totals'>): Cents =>
    NEGATIVE_DOCUMENT_TYPES.has(invoice.documentType)
        ? -Math.abs(invoice.totals.grossTotalCents)
        : invoice.totals.grossTotalCents;

// ═══════════════════════════════════════════════════════════════════════════
// Sicherheitseinbehalt auf Gegenbelegen
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Belegarten, die den Einbehalt des Ursprungsbelegs SPIEGELN.
 *
 * computeRetention gibt bei einem Leistungsstand <= 0 bewusst 0 zurueck: eine
 * gewoehnliche Rechnung mit Kundenguthaben behaelt nichts ein. Storno und
 * Korrektur sind der Gegenfall — sie nehmen einen Beleg ganz oder teilweise
 * zurueck, und mit ihm den Einbehalt (oder die Freigabe), den er getragen hat.
 * Ohne diese Spiegelung bliebe der Einbehalt des Ursprungsbelegs nach dem
 * Storno als Minderung stehen, obwohl die Forderung selbst aufgehoben ist: der
 * Zahlbetrag des Paares ginge nicht auf null auf.
 *
 * Die kaufmaennische Gutschrift gehoert ausdruecklich NICHT dazu (Plan §5.4).
 * Sie ist eine nachtraegliche Preisminderung und keine Ruecknahme des Belegs;
 * der Einbehalt des Ursprungsbelegs bleibt, wie er ist, bis die Schlussrechnung
 * ihn freigibt. Sie traegt deshalb 0 und keine Vorbelegliste.
 */
export const MIRRORED_RETENTION_DOCUMENT_TYPES = new Set<DocumentType>([
    'CANCELLATION_INVOICE',
    'CORRECTION_INVOICE',
]);

/**
 * Kehrt die Vorbelegliste eines Belegs um — das Gegenstueck zu
 * mirrorDeductions.
 *
 * Gebraucht beim Storno: der Ursprungsbeleg hat die Einbehalte seiner
 * Vorbelege abgezogen (und auf der Schlussrechnung freigegeben). Das Storno
 * muss dieselbe Liste gespiegelt tragen, damit seine Summen — gerechnet auf
 * den gespiegelten Zeilen und Abzuegen — exakt die negierten des Originals
 * sind, und damit prior auf der Kette stimmt: die stornierte Schlussrechnung
 * faellt als CANCELLED heraus, der Stornobeleg wird uebersprungen, die
 * Einbehalte sind wieder offen (openRetentionCents).
 *
 * Belegnummer, Datum und Herkunfts-Id bleiben stehen: sie benennen den
 * Vorgang, der rueckabgewickelt wird.
 */
export const mirrorPriorRetentions = (list: PriorRetention[]): PriorRetention[] =>
    list.map((r) => ({ ...r, retentionCents: -r.retentionCents }));

/**
 * `0 - x` statt `-x`: das Vorzeichen von 0 darf nicht kippen. `-0` ist fuer
 * toEqual und Object.is etwas anderes als 0 und stuende so im JSON der
 * Datenbank.
 */
const neg = (x: Cents): Cents => 0 - x;

/**
 * Die fuenf Einbehalt-Felder des Ursprungsbelegs, negiert — das, was ein
 * Storno in seine Summen uebernimmt (invoiceTotalsWithRetention, fuenfter
 * Parameter). Altbestand ohne die Felder spiegelt 0.
 */
export const mirrorRetentionTotals = (
    original: Pick<
        InvoiceTotals,
        | 'cumulativeRetentionCents'
        | 'priorRetentionCents'
        | 'retentionCents'
        | 'retentionReleaseCents'
        | 'paidPriorCents'
    >,
): RetentionTotals => ({
    cumulativeRetentionCents: neg(original.cumulativeRetentionCents ?? 0),
    priorRetentionCents: neg(original.priorRetentionCents ?? 0),
    retentionCents: neg(original.retentionCents ?? 0),
    retentionReleaseCents: neg(original.retentionReleaseCents ?? 0),
    paidPriorCents: neg(original.paidPriorCents ?? 0),
});

/**
 * Die Einbehalt-Angaben, die ein Gegenbeleg aus dem Ursprungsbeleg uebernimmt.
 *
 * Der EFFEKTIVE Satz des Ursprungsbelegs (effectiveRetentionPercent) und nicht
 * die heutige Vereinbarung — dieselbe Begruendung wie bei ancillaryPercent und
 * discountPercent in cancelInvoice: ein Storno muss den Beleg exakt aufheben.
 * Effektiv, weil eine freigebende Schlussrechnung mit Satz 0 gerechnet hat:
 * kopierte der Storno die 5 % vom Feld, hielte er auf den gespiegelten
 * Zeilen erneut ein, statt die Freigabe zurueckzudrehen.
 *
 * `releaseRetention: false` gehoert zwingend dazu: der Satz ist bereits der
 * effektive, und ein Gegenbeleg soll keine eigene Freigabe-Logik anwerfen.
 * Seine Vorbelegliste ist die GESPIEGELTE des Originals
 * (mirrorPriorRetentions) und nichts sonst.
 *
 * Die Gutschrift traegt ausdruecklich 0 — als Aussage, nicht als fehlender
 * Wert, damit eine spaetere Vorbelegung sie nicht nachtraeglich fuellt.
 */
export const copiedRetention = (
    documentType: DocumentType,
    original: Pick<Invoice, 'documentType' | 'details'>,
): Pick<InvoiceDetails, 'retentionPercent' | 'releaseRetention'> =>
    MIRRORED_RETENTION_DOCUMENT_TYPES.has(documentType)
        ? {
              retentionPercent: effectiveRetentionPercent(original.documentType, original.details),
              releaseRetention: false,
          }
        : { retentionPercent: 0, releaseRetention: false };

/**
 * Gesamtsummen EINSCHLIESSLICH Sicherheitseinbehalt — die eine Stelle, ueber die
 * der Service seine Summen bildet.
 *
 * Fuer jeden gewoehnlichen Beleg ist das genau calculateInvoiceTotals. Storno
 * und Korrektur (MIRRORED_RETENTION_DOCUMENT_TYPES) rechnen nicht neu, sondern
 * SPIEGELN:
 *
 *  - Mit `mirrored` (mirrorRetentionTotals des Originals) werden die fuenf
 *    Einbehalt-Felder wortwoertlich uebernommen. Das ist der Weg fuer das
 *    Storno: es hebt genau die Werte auf, die der Beleg getragen hat — auch
 *    dann, wenn das Original noch nach dem alten inkrementellen Modell
 *    gerechnet wurde.
 *  - Ohne `mirrored` und mit negativem Leistungsstand (gespiegelte Zeilen)
 *    wird die kumulative Formel auf den negierten Eingaben (gross, prepaid,
 *    prior) gerechnet und das Ergebnis negiert. Fuer ein Storno mit
 *    gespiegelten Zeilen, Abzuegen und Vorbelegliste ergibt das exakt die
 *    negierten Werte des Originals (gleiche Formel, gleiche Rundung); fuer
 *    die Korrektur folgt der Einbehalt damit der RICHTUNG der Korrektur —
 *    nach unten gibt sie Einbehalt zurueck, nach oben behaelt sie neu ein.
 *    Ursprung plus Korrektur tragen so den Einbehalt des berichtigten
 *    Betrags.
 *
 * Bei positivem Leistungsstand (Korrektur nach oben) rechnet der Gegenbeleg
 * wie ein gewoehnlicher Beleg.
 */
export const invoiceTotalsWithRetention = (
    documentType: DocumentType,
    lines: InvoiceLine[],
    deductions: AdvanceDeduction[] = [],
    retention?: RetentionInput,
    mirrored?: RetentionTotals,
): InvoiceTotals => {
    const totals = calculateInvoiceTotals(lines, deductions, retention);
    if (!MIRRORED_RETENTION_DOCUMENT_TYPES.has(documentType)) return totals;

    const withRetention = (r: RetentionTotals): InvoiceTotals => ({
        ...totals,
        ...r,
        payableCents:
            totals.grossTotalCents -
            totals.prepaidCents -
            r.retentionCents +
            r.retentionReleaseCents,
    });

    if (mirrored) return withRetention(mirrored);
    if (totals.grossTotalCents >= 0) return totals;

    const prior = totals.priorRetentionCents ?? 0;
    const r = computeRetention(
        -totals.grossTotalCents,
        -totals.prepaidCents,
        retention?.percent ?? 0,
        -prior,
    );
    return withRetention(mirrorRetentionTotals(r));
};
